import {
  fetchTagsConfig,
  renderBadges,
  type Badge,
  type FetchJson,
} from "./badgesShared";

type SearchResult = { badges?: Badge[]; sku?: string };
type SearchResponse = { results?: SearchResult[] };

const BADGES_SELECTOR = '[data-entity="ss-badges"]';
const FETCH_TIMEOUT_MS = 8000;

const fetchJson: FetchJson = <T>(url: string): Promise<T | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  return fetch(url, { credentials: "omit", signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));
};

async function fetchBadges(siteId: string, sku: string): Promise<Badge[]> {
  const encodedSiteId = encodeURIComponent(siteId);
  const encodedSku = encodeURIComponent(sku);
  const url = `https://api.searchspring.net/api/search/search.json?siteId=${encodedSiteId}&resultsFormat=native&resultsPerPage=10&q=${encodedSku}`;
  const data = await fetchJson<SearchResponse>(url);
  const results = data?.results;
  if (!results || !results.length) return [];

  const exactMatch = results.find((result) => result.sku === sku);
  const source = exactMatch ?? results[0];

  return source.badges && Array.isArray(source.badges) ? source.badges : [];
}

async function mountBadges(containerElement: HTMLElement) {
  if (
    containerElement.dataset.ssBadgesBound === "true" ||
    containerElement.childElementCount
  )
    return;
  const { id: sku, siteId } = containerElement.dataset;
  if (!sku || !siteId) return;

  containerElement.dataset.ssBadgesBound = "true";
  const [fetchedBadges, tagsConfig] = await Promise.all([
    fetchBadges(siteId, sku),
    fetchTagsConfig(siteId, fetchJson),
  ]);
  const hideSoldOut = containerElement.dataset.hideSoldOut === "true";
  const badges = hideSoldOut
    ? fetchedBadges.filter((badge) => {
        const normalizedTag = (badge.tag ?? "").trim().toLowerCase();
        const normalizedValue = (badge.value ?? "").trim().toLowerCase();
        return (
          normalizedTag !== "sold-out" &&
          normalizedTag !== "soldout" &&
          !normalizedTag.includes("sold out") &&
          !normalizedValue.includes("sold out")
        );
      })
    : fetchedBadges;
  if (badges.length) renderBadges(containerElement, badges, tagsConfig);
}

function mountBadgesIn(rootNode: ParentNode) {
  rootNode.querySelectorAll(BADGES_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) void mountBadges(element);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => mountBadgesIn(document));
} else {
  mountBadgesIn(document);
}

document.addEventListener("shopify:section:load", (event) => {
  const targetElement = event.target;
  if (targetElement instanceof HTMLElement) mountBadgesIn(targetElement);
});
